import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, HostBinding, HostListener, inject, DestroyRef, Inject, NgModule, ContentChild, ContentChildren, ViewContainerRef, ChangeDetectorRef, TemplateRef } from '@angular/core';
import { takeUntil, tap, map } from 'rxjs/operators';
import { Subject, of, startWith, fromEvent, firstValueFrom, BehaviorSubject, merge, map as map$1, scan } from 'rxjs';
import { ConfirmModalTermsComponent } from '@fgb/portal-component-library/src/lib/shared/confirm-modal-terms';
import * as i1 from '@ng-bootstrap/ng-bootstrap';
import * as i1$1 from '@fgb/core';
import { config, LOYALTY_STATUS, AuctionItem, MarketplaceItemExtended, LottoItem } from '@fgb/core';
import * as i1$2 from '@ngx-translate/core';
import * as i1$3 from 'ngx-owl-carousel-o';
import { CarouselComponent } from 'ngx-owl-carousel-o';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i1$4 from '@fgb/portal-component-library/src/lib/shared/services';
import { sortMultiple } from '@fgb/portal-component-library/src/lib/shared/utilities';

class ConfirmModalTermsDirective {
    get isDisabled() {
        return this.disabled ? '' : null;
    }
    constructor(modalService) {
        this.modalService = modalService;
        this.valid = true;
        this.disabled = false;
        this.confirm = new EventEmitter();
        this.destroy$ = new Subject();
    }
    onClick() {
        if (!this.disabled) {
            if (this.cancelModal)
                return;
            this.modal = this.modalService.open(ConfirmModalTermsComponent, {
                size: !!this.confirmImage ? 'lg' : undefined,
                centered: true,
            });
            this.modalComponent = this.modal.componentInstance;
            this.modalComponent.modalContent = this.confirmContent;
            this.modalComponent.imageRef = this.confirmImage;
            this.modalComponent.termsAndCondtions = this.termsAndCondtions;
            this.modalComponent.close.pipe(takeUntil(this.destroy$)).subscribe((x) => {
                this.modal.close(x);
            });
            this.modalComponent.confirm.pipe(takeUntil(this.destroy$)).subscribe((x) => {
                this.confirm.emit(x);
                this.modal.close(x);
            });
        }
    }
    ngOnChanges() {
        if (!this.valid) {
            this.modal.close();
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: ConfirmModalTermsDirective, deps: [{ token: i1.NgbModal }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: ConfirmModalTermsDirective, selector: "[confirmModalTerms]", inputs: { termsAndCondtions: "termsAndCondtions", cancelModal: "cancelModal", confirmImage: "confirmImage", confirmContent: "confirmContent", valid: "valid", disabled: "disabled" }, outputs: { confirm: "confirm" }, host: { listeners: { "click": "onClick()" }, properties: { "attr.disabled": "this.isDisabled" } }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: ConfirmModalTermsDirective, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line: directive-selector
                    selector: '[confirmModalTerms]',
                }]
        }], ctorParameters: () => [{ type: i1.NgbModal }], propDecorators: { termsAndCondtions: [{
                type: Input
            }], cancelModal: [{
                type: Input
            }], confirmImage: [{
                type: Input
            }], confirmContent: [{
                type: Input
            }], valid: [{
                type: Input
            }], disabled: [{
                type: Input
            }], confirm: [{
                type: Output
            }], isDisabled: [{
                type: HostBinding,
                args: ['attr.disabled']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

var ScreenType;
(function (ScreenType) {
    ScreenType[ScreenType["Mobile"] = 0] = "Mobile";
    ScreenType[ScreenType["Desktop"] = 1] = "Desktop";
})(ScreenType || (ScreenType = {}));
/** Creates the view only for the selected window size.
 * Will only create the view once even if window size changes, afterwards it is simply detached/reinserted.
 * Usage: *ifScreenSize="ScreenType.Mobile" or *ifScreenSize="ScreenType.Desktop"
 * */
class IfScreenSizeDirective {
    constructor(templateRef, viewContainer, screenSizeService) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.screenSizeService = screenSizeService;
    }
    set ifScreenSize(media) {
        this.resizeSubscription = this.screenSizeService
            .selectScreenWidth()
            .pipe(tap((width) => this.resizeCheck(width, media)))
            .subscribe();
    }
    /** Creates Embedded View */
    createView() {
        this.viewContainer.createEmbeddedView(this.templateRef);
        this.embeddedView = this.viewContainer.get(0);
    }
    /** On selected media, creates or display existing view.
     * Otherwise, detaches the view without destroying it.
     */
    resizeCheck(width, media) {
        if (this.screenMatchesSelector(width, media)) {
            if (this.embeddedView) {
                this.viewContainer.insert(this.embeddedView);
            }
            else {
                this.createView();
            }
        }
        else {
            this.viewContainer.detach();
        }
    }
    /** Checks if the screen width matches the selected media option */
    screenMatchesSelector(width, media) {
        const mobileBreakPoint = this.getDesktopBreakpointValue();
        const mobileCheck = media == ScreenType.Mobile && width < mobileBreakPoint;
        const desktopCheck = media == ScreenType.Desktop && width >= mobileBreakPoint;
        return mobileCheck || desktopCheck;
    }
    /** Get the desktop breakpoint from bootstrap variables defined as $grid-breakpoints */
    getDesktopBreakpointValue() {
        const bodyStyles = window.getComputedStyle(document.body);
        const mobileBreakpointSASSValue = bodyStyles.getPropertyValue('--bs-breakpoint-lg');
        const mobileBreakpointNumber = +mobileBreakpointSASSValue.replace('px', '');
        return mobileBreakpointNumber;
    }
    ngOnDestroy() {
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: IfScreenSizeDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i1$1.ScreenSizeService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: IfScreenSizeDirective, selector: "[ifScreenSize]", inputs: { ifScreenSize: "ifScreenSize" }, host: { listeners: { "window:beforeunload": "ngOnDestroy()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: IfScreenSizeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ifScreenSize]',
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i1$1.ScreenSizeService }], propDecorators: { ifScreenSize: [{
                type: Input
            }], ngOnDestroy: [{
                type: HostListener,
                args: ['window:beforeunload']
            }] } });

/**
 * Enables a default image source to be specified that will be shown upon image retrieval error (i.e. HTTP 404).
 * Usage:
 *  <img src="https://www.normal-image-address.com/image.jpg" useDefault="true"></img>
 */
class ImgDefaultDirective {
    constructor() {
        this.imageLoaded = false;
        this.imageError = false;
    }
    ngOnInit() {
        if (!this.loyaltyType) {
            this.DefaultImgSrc = `${config.contentUrl}/images/noimage.png`;
        }
        else {
            switch (this.loyaltyType) {
                case 'marketplace': {
                    this.DefaultImgSrc = 'assets/images/placeholders/marketplace-item.png';
                    break;
                }
                case 'auction': {
                    this.DefaultImgSrc = 'assets/images/placeholders/auction-item.png';
                    break;
                }
                case 'lotto': {
                    this.DefaultImgSrc = 'assets/images/placeholders/lotto-item.png';
                    break;
                }
                case 'wallet': {
                    this.DefaultImgSrc = 'assets/images/placeholders/marketplace-item.png';
                    break;
                }
                case 'badge': {
                    this.DefaultImgSrc = 'assets/images/placeholders/badge-item.png';
                    break;
                }
                default: {
                    this.DefaultImgSrc = 'assets/images/placeholders/marketplace-item.png';
                    break;
                }
            }
        }
    }
    /**
     * Called on error. Sets the default image source if required and the class to 'image-error'.
     */
    updateUrl() {
        if (this.useDefault) {
            this.src = this.DefaultImgSrc;
        }
        this.imageError = true;
    }
    /**
     * Called on success. Leaves the requested image source as-is and sets the class to 'image-loaded'.
     */
    load() {
        this.imageLoaded = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: ImgDefaultDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: ImgDefaultDirective, selector: "img[useDefault]", inputs: { src: "src", useDefault: "useDefault", loyaltyType: "loyaltyType" }, host: { listeners: { "error": "updateUrl()", "load": "load()" }, properties: { "attr.src": "this.src", "class.image-loaded": "this.imageLoaded", "class.image-error": "this.imageError" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: ImgDefaultDirective, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line: directive-selector
                    selector: 'img[useDefault]',
                }]
        }], ctorParameters: () => [], propDecorators: { src: [{
                type: HostBinding,
                args: ['attr.src']
            }, {
                type: Input
            }], useDefault: [{
                type: Input
            }], loyaltyType: [{
                type: Input
            }], imageLoaded: [{
                type: HostBinding,
                args: ['class.image-loaded']
            }], imageError: [{
                type: HostBinding,
                args: ['class.image-error']
            }], updateUrl: [{
                type: HostListener,
                args: ['error']
            }], load: [{
                type: HostListener,
                args: ['load']
            }] } });

class NgSubscribeContext {
    constructor() {
        this.ngSubscribe = null;
    }
}
class NgSubscribeDirective {
    set ngSubscribe(inputObservable) {
        if (this.observable !== inputObservable) {
            this.observable = inputObservable;
            this.subscription && this.subscription.unsubscribe();
            this.subscription = this.observable.subscribe((value) => {
                this.context.ngSubscribe = value;
                this.cdr.markForCheck();
            });
        }
    }
    constructor(viewContainer, cdr, templateRef) {
        this.viewContainer = viewContainer;
        this.cdr = cdr;
        this.templateRef = templateRef;
        this.context = new NgSubscribeContext();
    }
    ngOnInit() {
        this.viewContainer.createEmbeddedView(this.templateRef, this.context);
    }
    ngOnDestroy() {
        this.subscription && this.subscription.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NgSubscribeDirective, deps: [{ token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }, { token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: NgSubscribeDirective, selector: "[ngSubscribe]", inputs: { ngSubscribe: "ngSubscribe" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NgSubscribeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngSubscribe]',
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }, { type: i0.TemplateRef }], propDecorators: { ngSubscribe: [{
                type: Input
            }] } });

/**
 * This directive is used to hide features
 * throughout the portal depending on the
 * given feature flag set within website content.
 *
 * Example of usage:
 * <ng-container *fgbWebsiteContentFeatureFlag="'feature.flag.member.card'"> ... </ng-container>
 */
class WebsiteContentFeatureFlagDirective {
    constructor(translate, templateRef, viewContainer, log) {
        this.translate = translate;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.log = log;
        this._isDestroyed$ = new Subject();
    }
    ngOnInit() {
        this.isFeatureEnabled(this.featureKey)
            .pipe(takeUntil(this._isDestroyed$))
            .subscribe((result) => {
            if (result) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            }
        });
    }
    isFeatureEnabled(featureKey) {
        if (!featureKey) {
            this.log.debug('[WSCFeatureFlagDirective] No feature key provided');
            return of(false);
        }
        return this.translate.stream(featureKey).pipe(tap((data) => {
            this.log.debug(`[WSCFeatureFlagDirective] Checking feature is actviated for "${featureKey}" has value "${data}"`);
        }), map((data) => data === 'true'));
    }
    ngOnDestroy() {
        this._isDestroyed$.next();
        this._isDestroyed$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: WebsiteContentFeatureFlagDirective, deps: [{ token: i1$2.TranslateService }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i1$1.LoggingService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: WebsiteContentFeatureFlagDirective, selector: "[fgbWebsiteContentFeatureFlag]", inputs: { featureKey: ["fgbWebsiteContentFeatureFlag", "featureKey"] }, host: { listeners: { "window:beforeunload": "ngOnDestroy()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: WebsiteContentFeatureFlagDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fgbWebsiteContentFeatureFlag]',
                }]
        }], ctorParameters: () => [{ type: i1$2.TranslateService }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i1$1.LoggingService }], propDecorators: { featureKey: [{
                type: Input,
                args: ['fgbWebsiteContentFeatureFlag']
            }], ngOnDestroy: [{
                type: HostListener,
                args: ['window:beforeunload']
            }] } });

/**
 * This directive is used to grab the background color of an image and set it as the background color of the parent element.
 * @param auto - If true, the background color of the parent element will be set automatically.
 * @param targetPosition - The position of the pixel to grab the color from.
 */
class ImgBackgroundGrabberDirective {
    onLoad(event) {
        this.bgColor = this.getImageBgColor();
        // If auto is true, set the background color of the parent element.
        if (this.auto && this._el.nativeElement.parentNode) {
            this._renderer.setStyle(this._el.nativeElement.parentNode, "background-color", this.bgColor);
        }
    }
    constructor(_el, _renderer) {
        this._el = _el;
        this._renderer = _renderer;
        this.bgColor = "#ffffff";
        this.auto = true;
        this.targetPosition = { x: 0, y: 0 };
    }
    getImageBgColor() {
        let canvas = document.createElement("canvas");
        const el = this._el.nativeElement;
        let ctx = canvas.getContext("2d");
        ctx.drawImage(el, 0, 0, el.clientWidth, el.clientHeight);
        const i = ctx.getImageData(this.targetPosition.x, this.targetPosition.y, el.clientWidth, el.clientHeight).data;
        return '#' + ((1 << 24) + (i[0] << 16) + (i[1] << 8) + i[2]).toString(16).slice(1);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: ImgBackgroundGrabberDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: ImgBackgroundGrabberDirective, selector: "img[imgBackgroundGrabber]", inputs: { auto: "auto", targetPosition: "targetPosition" }, host: { listeners: { "load": "onLoad($event)" } }, exportAs: ["imgBackgroundGrabber"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: ImgBackgroundGrabberDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "img[imgBackgroundGrabber]",
                    exportAs: "imgBackgroundGrabber"
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { auto: [{
                type: Input
            }], targetPosition: [{
                type: Input
            }], onLoad: [{
                type: HostListener,
                args: ["load", ["$event"]]
            }] } });

/**
 * Directive to add custom styles to the dots of the carousel
 * in the carousel options, set dots: true and autoWidth: true for this directive to work.
 * overrides the default dot behavior of the owl-carousel to scroll 1 dot per 1 item
 */
class CustomOwlCarousel {
    get customClass() {
        return this._isCustom;
    }
    constructor(carousel, _el) {
        this.carousel = carousel;
        this._el = _el;
        this._destoryRef = inject(DestroyRef);
    }
    ngOnInit() {
        const options = this.carousel.options;
        if (options.dots && options.autoWidth) {
            this._isCustom = true;
        }
    }
    ngAfterViewInit() {
        this.carousel.changed
            .pipe(startWith({ startPosition: 0 }), takeUntilDestroyed(this._destoryRef))
            .subscribe(({ startPosition }) => {
            if (this.carousel.options.dots && this.carousel.options.autoWidth) {
                const position = startPosition || 0;
                const dotsContainer = this._el.nativeElement.getElementsByClassName('owl-dots')[0];
                const dots = this._el.nativeElement.getElementsByClassName('owl-dot');
                if (dotsContainer) {
                    dotsContainer.scrollLeft =
                        dots.item(position)?.offsetLeft -
                            dotsContainer.getBoundingClientRect().width / 2 +
                            dots.item(position).getBoundingClientRect().width / 2;
                }
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CustomOwlCarousel, deps: [{ token: CarouselComponent }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: CustomOwlCarousel, selector: "owl-carousel-o", host: { properties: { "class.owl-custom": "this.customClass" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: CustomOwlCarousel, decorators: [{
            type: Directive,
            args: [{
                    selector: 'owl-carousel-o',
                }]
        }], ctorParameters: () => [{ type: i1$3.CarouselComponent, decorators: [{
                    type: Inject,
                    args: [CarouselComponent]
                }] }, { type: i0.ElementRef }], propDecorators: { customClass: [{
                type: HostBinding,
                args: ['class.owl-custom']
            }] } });

class DirectivesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DirectivesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.1", ngImport: i0, type: DirectivesModule, declarations: [IfScreenSizeDirective,
            WebsiteContentFeatureFlagDirective,
            ImgDefaultDirective,
            ConfirmModalTermsDirective,
            NgSubscribeDirective,
            ImgBackgroundGrabberDirective,
            CustomOwlCarousel], exports: [IfScreenSizeDirective,
            WebsiteContentFeatureFlagDirective,
            ImgDefaultDirective,
            ConfirmModalTermsDirective,
            NgSubscribeDirective,
            ImgBackgroundGrabberDirective,
            CustomOwlCarousel] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DirectivesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DirectivesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        IfScreenSizeDirective,
                        WebsiteContentFeatureFlagDirective,
                        ImgDefaultDirective,
                        ConfirmModalTermsDirective,
                        NgSubscribeDirective,
                        ImgBackgroundGrabberDirective,
                        CustomOwlCarousel
                    ],
                    exports: [
                        IfScreenSizeDirective,
                        WebsiteContentFeatureFlagDirective,
                        ImgDefaultDirective,
                        ConfirmModalTermsDirective,
                        NgSubscribeDirective,
                        ImgBackgroundGrabberDirective,
                        CustomOwlCarousel
                    ],
                    imports: [],
                }]
        }] });

class NavItemElement {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.rightPos = 0;
    }
    ngAfterViewInit() {
        this.rightPos =
            this.elementRef.nativeElement.getBoundingClientRect().left + this.elementRef.nativeElement.getBoundingClientRect().width;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavItemElement, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: NavItemElement, selector: ".nav-item:not(.more-dropdown)", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavItemElement, decorators: [{
            type: Directive,
            args: [{
                    selector: '.nav-item:not(.more-dropdown)',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });
class MoreDropdownElement {
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MoreDropdownElement, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: MoreDropdownElement, selector: ".nav-item.more-dropdown", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: MoreDropdownElement, decorators: [{
            type: Directive,
            args: [{
                    selector: '.nav-item.more-dropdown',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });
class NavBarElement {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.wordBreak = 'break-word';
        this.navRightPos = 0;
        this._destroyed$ = new Subject();
    }
    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
    ngAfterViewInit() {
        this._updateNavigationMenu();
        fromEvent(window, 'resize')
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => {
            this._updateNavigationMenu();
        });
    }
    _updateNavigationMenu() {
        if (!this.moreDropdown) {
            return;
        }
        this.navRightPos =
            this.elementRef.nativeElement.getBoundingClientRect().left +
                (this.elementRef.nativeElement.getBoundingClientRect().width -
                    this.moreBtn.elementRef.nativeElement.getBoundingClientRect().width);
        this.navItems.forEach((item) => {
            // If nav item right position is greater than navbar right position, append the item to the more dropdown menu, else append it to the navbar
            if (item.rightPos > this.navRightPos) {
                if (!item.elementRef.nativeElement.classList.contains('static-nav-item')) {
                    this.renderer.appendChild(this.moreDropdown.nativeElement, item.elementRef.nativeElement);
                }
            }
            else {
                if (!item.elementRef.nativeElement.classList.contains('static-nav-item')) {
                    this.renderer.appendChild(this.elementRef.nativeElement, item.elementRef.nativeElement);
                }
            }
            // if item contains static-nav-item class it will be appended just before the more button dropdown
            if (item.elementRef.nativeElement.classList.contains('static-nav-item')) {
                this.renderer.appendChild(this.elementRef.nativeElement, item.elementRef.nativeElement);
            }
        });
        // If more dropdown has items, append the more button to the navbar
        if (this.moreDropdown.nativeElement.children.length > 0) {
            this.renderer.appendChild(this.elementRef.nativeElement, this.moreBtn.elementRef.nativeElement);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavBarElement, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: NavBarElement, selector: ".navbar-nav", host: { properties: { "style.word-break": "this.wordBreak" } }, queries: [{ propertyName: "moreBtn", first: true, predicate: MoreDropdownElement, descendants: true }, { propertyName: "moreDropdown", first: true, predicate: ["greedyDropdown"], descendants: true, static: true }, { propertyName: "navItems", predicate: NavItemElement, read: NavItemElement }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavBarElement, decorators: [{
            type: Directive,
            args: [{
                    selector: '.navbar-nav',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { moreBtn: [{
                type: ContentChild,
                args: [MoreDropdownElement, { static: false }]
            }], navItems: [{
                type: ContentChildren,
                args: [NavItemElement, { read: NavItemElement }]
            }], wordBreak: [{
                type: HostBinding,
                args: ['style.word-break']
            }], moreDropdown: [{
                type: ContentChild,
                args: ['greedyDropdown', { static: true }]
            }] } });

class NavbarDirectivesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavbarDirectivesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.1", ngImport: i0, type: NavbarDirectivesModule, declarations: [NavBarElement,
            MoreDropdownElement,
            NavItemElement], exports: [NavBarElement,
            MoreDropdownElement,
            NavItemElement] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavbarDirectivesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NavbarDirectivesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NavBarElement,
                        MoreDropdownElement,
                        NavItemElement
                    ],
                    exports: [
                        NavBarElement,
                        MoreDropdownElement,
                        NavItemElement
                    ]
                }]
        }] });

/**
 * Directive to show or hide an element based on the status of an item.
 * Optionally, you can pass or override an array of disabled statuses to disable the element if the item has one of the statuses.
 * Similar to ngIf else, you can also pass a template to show when the item is disabled.
 *
 * @example
 *  <ng-container *ifItemEnabled="item; else disabledTemplate">
 *   <div>I am enabled</div>
 * </ng-container>
 *
 * <ng-template #disabledTemplate let-status>
 *  <div>I am disabled! {{ status.data | json }}</div>
 * </ng-template>
 *
 * using the directive exportAs, you can access the status of the item and refresh method by adding #itemEnabled="itemEnabled" to the ng-template,
 * you can also force refresh the item from the api by passing true to the refresh method ex. itemEnabled.refresh(true)
 * @example
 *     <ng-template
            ifItemEnabled
            [ifItemEnabled]="lottoItem"
            [ifItemEnabledElse]="disabledTemplate"
            #itemEnabled="itemEnabled"
          >
            <button (click)="itemEnabled.refresh()">test</button>
      </ng-template>
 */
class IfItemEnabled {
    constructor(templateRef, viewContainer, lottoService, lottoQuery, marketplaceService, marketplaceQuery, auctionService, auctionQuery, statusService) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.lottoService = lottoService;
        this.lottoQuery = lottoQuery;
        this.marketplaceService = marketplaceService;
        this.marketplaceQuery = marketplaceQuery;
        this.auctionService = auctionService;
        this.auctionQuery = auctionQuery;
        this.statusService = statusService;
        this.isDisabled = false;
        /**
         * Default disabled statuses
         * you can override this by passing an array of statuses to ifItemEnabledDisabledStatus
         * ex. *ifItemEnabled="item; disabledStatus: [RewardStatusType.SOLD_OUT, RewardStatusType.COMING_SOON]"
         * if the item has one of the statuses, the element will be marked as disabled
         */
        this.disabledStatus = [LOYALTY_STATUS.SOLD_OUT, LOYALTY_STATUS.COMING_SOON, LOYALTY_STATUS.LIMIT_REACHED, LOYALTY_STATUS.NOT_ENOUGH_POINTS];
    }
    set ifItemEnabled(item) {
        if (this.item !== item) {
            this.item = item;
            this.createView();
        }
    }
    /**
     * Override the default disabled statuses, pass an array of statuses to disable the element if the item has one of the statuses.
     */
    set ifItemEnabledDisabledStatus(statuses) {
        this.disabledStatus = statuses;
    }
    /**
     * Similar to ngIf else, pass a template to show when the item is disabled
     * ex. <ng-container *ifItemEnabled="auction(); else disabledTemplate">
     *     ...
     *     </ng-container>
     *
     * <ng-template #disabledTemplate let-status>
     *    <div>I am disabled! {{ status.data | json }}</div>
     * </ng-template>
     */
    set ifItemEnabledElse(templateRef) {
        this.elseTemplate = templateRef;
    }
    /**
     * Create the view once all input variables are initialized
     */
    ngOnInit() {
        this.createView();
    }
    createView() {
        this.viewContainer.clear();
        this.checkStatus();
        // Check if the item has one of the disabled statuses
        this.isDisabled = this.disabledStatus.some(status => this.status.status === status);
        // If the item is enabled, create the view
        if (!this.isDisabled) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
        else {
            // If else template is provided, create the else view
            if (this.elseTemplate) {
                /**
                 * Pass data and refresh function to the else template, add let-status to ng-template
                 * ex. <ng-template #disabledTemplate let-status>
                 *        <div>I am disabled! {{ status.data | json }}</div>
                 *        <button (click)="status.refresh()">Refresh Me Status</button>
                 *     </ng-template>
                 */
                const contextContent = { data: this.status, type: this.status.status, refresh: this.refresh.bind(this) };
                const context = { $implicit: contextContent };
                this.viewContainer.createEmbeddedView(this.elseTemplate, context);
            }
        }
    }
    /**
     * Initialize the status of the item
     */
    checkStatus() {
        this.status = this.statusService.getStatus(this.item);
    }
    /**
     * Refresh the view, useful when the status of the item changes ex. coming soon status
     * you can call this function to refresh the view in inside the else template
     * @example <ng-template #disabledTemplate let-status>
     *            <button (click)="status.refresh()">Refresh Me</button>
     *          </ng-template>
     * @see {@link RewardLottoStatus} {@link RewardMarketplaceStatus} {@link RewardAuctionStatus}
     * for more information on how the status is determined and how the coming soon status is handled
     *
     * @param force - forces to refresh the item from the api then assign the new item to the directive and refresh the view
     * force is not tested yet for different item types
     * set force to true if you want to refresh the item from the api
     *
     * @example <ng-template #disabledTemplate let-status>
     *            <button (click)="status.refresh(true)">Refresh Me From API</button>
     *          </ng-template>
     */
    refresh(force = false) {
        if (!force) {
            this.createView();
            return;
        }
        let promise;
        let getItem;
        let id;
        /**
         * Check the type of the item and fetch the item from the api
         */
        if (this.item instanceof AuctionItem) {
            id = this.item.Id;
            promise = firstValueFrom(this.auctionService.fetchAuction(id));
            getItem = this.auctionQuery.getEntity.bind(this.auctionQuery);
        }
        else if (this.item instanceof MarketplaceItemExtended) {
            id = this.item.Id;
            promise = firstValueFrom(this.marketplaceService.fetchMarketplaceItemById(id));
            getItem = this.marketplaceQuery.getEntity.bind(this.marketplaceQuery);
        }
        else if (this.item instanceof LottoItem) {
            id = this.item.LottoId;
            promise = firstValueFrom(this.lottoService.fetchLottoById(id));
            getItem = this.lottoQuery.getEntity.bind(this.lottoQuery);
        }
        if (promise) {
            promise.then(() => {
                // Get the updated item from api, not tested yet but should fetch item from api
                this.item = getItem(id);
                // Then recreate the view to update status
                this.createView();
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: IfItemEnabled, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: i1$1.LottoService }, { token: i1$1.LottoQuery }, { token: i1$1.MarketplaceService }, { token: i1$1.MarketplaceQuery }, { token: i1$1.AuctionService }, { token: i1$1.AuctionQuery }, { token: i1$1.LoyaltyStatusService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: IfItemEnabled, isStandalone: true, selector: "[ifItemEnabled]", inputs: { ifItemEnabled: "ifItemEnabled", ifItemEnabledDisabledStatus: "ifItemEnabledDisabledStatus", ifItemEnabledElse: "ifItemEnabledElse" }, exportAs: ["itemEnabled"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: IfItemEnabled, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ifItemEnabled]',
                    standalone: true,
                    exportAs: 'itemEnabled'
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: i1$1.LottoService }, { type: i1$1.LottoQuery }, { type: i1$1.MarketplaceService }, { type: i1$1.MarketplaceQuery }, { type: i1$1.AuctionService }, { type: i1$1.AuctionQuery }, { type: i1$1.LoyaltyStatusService }], propDecorators: { ifItemEnabled: [{
                type: Input
            }], ifItemEnabledDisabledStatus: [{
                type: Input
            }], ifItemEnabledElse: [{
                type: Input
            }] } });

/**
 * Reusable directive to hide mobile navigation when added to a component. Add it to the hostDirectives array of a component
 * to hide the mobile navigation when the component is loaded and restore the default navigation settings when the component is destroyed.
 * @example
 * Component({
 *     selector: 'fgb-auction-detail-page',
 *     templateUrl: './auction-detail-page.component.html',
 *     styleUrls: ['./auction-detail-page.component.scss'],
 *     hostDirectives: [
 *       HideMobileNav
 *     ]
 *   })
 *   ...
 */
class HideMobileNav {
    constructor(navigationService) {
        this.navigationService = navigationService;
    }
    ngOnInit() {
        setTimeout(() => {
            // Hide mobile navigation upon component initialization
            this.navigationService.hideMobileNav();
        });
    }
    ngOnDestroy() {
        // Show mobile navigation upon component destruction
        this.navigationService.showMobileNav();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: HideMobileNav, deps: [{ token: i1$4.NavigationService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: HideMobileNav, isStandalone: true, selector: "[hideMobileNav]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: HideMobileNav, decorators: [{
            type: Directive,
            args: [{
                    selector: '[hideMobileNav]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i1$4.NavigationService }] });

/**
 * This function creates a data source object and initializes the data source only when the load method is called
 */
function createDataSource(options // setup up default options for sorting, filtering, etc...
) {
    return {
        data: null,
        total: 0,
        sort: options?.sort || null,
        pagination: {
            pageNumber: undefined,
            pageSize: undefined,
            entities: {},
            pages: [],
            collection: null,
            total: 0,
            isEmpty: true
        },
        // Filter to be implemented
        load() {
            if (this.data === null || this.data === undefined) {
                return this;
            }
            const type = Object.prototype.toString.call(this.data);
            if (type === '[object Array]') {
                // Load the data only when its not yet loaded then set the loaded status to true
                if (!this.loaded) {
                    let data = this.data.length > 0 ? this.data : null;
                    let total = data ? data.length : 0;
                    const rawData = data;
                    let pageEntities = {};
                    let pages = [];
                    // default page number on load
                    const pageNumber = 1;
                    if (data && this.sort) {
                        data = data.sort(sortMultiple(...Object.values(this.sort)));
                    }
                    if (options?.pageSize) {
                        pageEntities = this.setPages(options.pageSize);
                        pages = Object.keys(pageEntities).map(key => +key);
                        data = pageEntities[pageNumber];
                    }
                    return {
                        ...this,
                        data,
                        total,
                        loaded: true,
                        pagination: {
                            pageNumber,
                            pageSize: options?.pageSize,
                            entities: pageEntities,
                            pages,
                            collection: rawData,
                            total: rawData?.length || 0,
                            isEmpty: rawData?.length === 0
                        }
                    };
                }
            }
            return this;
        },
        setPages(pageSize) {
            // Set the page size
            let pageEntities = {};
            if (this.data) {
                const numberOfPages = Math.ceil(this.data.length / pageSize);
                for (let i = 0; i < numberOfPages; i++) {
                    pageEntities[i + 1] = this.data.slice(i * pageSize, (i + 1) * pageSize);
                }
            }
            return pageEntities;
        },
        goToPage(pageNo) {
            // Go to a specific page
            if (this.pagination.entities[pageNo]) {
                return {
                    ...this,
                    data: this.pagination.entities[pageNo],
                    pagination: {
                        ...this.pagination,
                        pageNumber: pageNo
                    }
                };
            }
            return {
                ...this,
                pagination: {
                    ...this.pagination,
                    pageNumber: undefined
                },
                data: []
            };
        },
        update(data = this.data, sort) {
            // Update the data source only when the data is already loaded
            if (this.loaded) {
                const type = Object.prototype.toString.call(data);
                if (data !== null && data !== undefined) {
                    if (type === '[object Array]') {
                        let val = data;
                        if (this.sort) {
                            val = val.sort(sortMultiple(...Object.values(this.sort)));
                        }
                        const total = this.pagination.collection?.length || val.length;
                        return {
                            ...this,
                            sort: sort || this.sort,
                            data: val,
                            total
                        };
                    }
                }
            }
            return {
                ...this,
                sort: sort || this.sort
            };
        },
        setSort(sort) {
            // Update the sort options
            return this.update(this.data, sort);
        },
        loaded: false
    };
}
/**
 * Function to load the data source
 */
function loadDataSource(dataSource) {
    return dataSource.load();
}
/**
 * Reusable directive to handle data source events with sorting and filtering(not implemented yet)
 * Usage:
 *
 * @example
 *
 *   <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [sortOptions]="sortOptions">
 *
 *     // Add ng-template to display the data, the let-items refers to the data source data is already sorted depending on the sort options passed in the
 *     // [sortOptions] input, the let-loaded refers to the loaded status of the data source
 *
 *     <ng-template let-items let-loaded="loaded">
 *
 *       // Check if the data source is loaded, if not display the loading template
 *       <ng-container *ngIf="loaded; else loading">
 *
 *           // Loop through the items and display the items
 *           <ng-container *ngFor="let item of items">
 *               <div class="loyalty-item-container">
 *                 <fgbcl-mixed-loyalty-item [item]="item"></fgbcl-mixed-loyalty-item>
 *               </div>
 *           </ng-container>
 *
 *       </ng-container>
 *     </ng-template>
 *
 *     <ng-template #loading>loading...</ng-template>
 *  </div>
 *
 *  Sortings can be passed in the [sortOptions] input
 *  @example
 *  sortOptions = {
 *     status: sortBy((a: MixedLoyaltyItem, b: MixedLoyaltyItem) => a.status.status - b.status.status),
 *     ordinal: sortBy((a: MixedLoyaltyItem, b: MixedLoyaltyItem) => a.data.Ordinal - b.data.Ordinal)
 *   };
 *
 *  pass the input to sortOptions ex. <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [sortOptions]="sortOptions">
 *
 *  Changing the sort direction example
 *
 *  @example
 *
 *  sortDirection: 'asc' | 'desc' = 'asc';
 *
 *  toggleSort(): void{
 *
 *    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
 *
 *    this.sortOptions = {
 *     status: sortBy((a: MixedLoyaltyItem, b: MixedLoyaltyItem) => a.status.status - b.status.status, this.sortDirection),
 *     ordinal: sortBy('name', this.sortDirection)
 *   };
 *  }
 *
 *  expect the data source to be sorted with the new toggled direction
 */
class DataSourceDirective {
    set dataSource(value) {
        if (value) {
            this._dataSubject.next(value);
        }
    }
    set sortOptions(options) {
        this._sortSubject.next(options);
    }
    ;
    set page(page) {
        this._pageSubject.next(page);
    }
    constructor() {
        this.dataSourceChange = new EventEmitter();
        this.paginationChange = new EventEmitter();
        this.isEmpty = false;
        this._dataSubject = new BehaviorSubject([]);
        this._sortSubject = new BehaviorSubject(null);
        this._pageSubject = new Subject();
        this.vc = inject(ViewContainerRef);
        this._cd = inject(ChangeDetectorRef);
        this._destroyRef = inject(DestroyRef);
    }
    ngOnInit() {
        // Merge events
        merge(this.onDataSourceChanged.pipe(map$1(this.dataHandler)), this.onSortChanged.pipe(map$1(this.sortHandler)), this.onPageChanged.pipe(map$1(this.pageHandler))).pipe(
        // Handle the data source events, set the initial data source with empty data
        scan(this.eventHandler, createDataSource({ pageSize: this.pageSize })), takeUntilDestroyed(this._destroyRef)).subscribe(ds => {
            // Set data and loaded values
            this.data = ds.data;
            this.isLoaded = ds.loaded && ds.data !== null;
            this.isEmpty = ds.data !== null && ds.total === 0;
            if (!ds.pagination.isEmpty) {
                this.paginationChange.emit(ds.pagination);
            }
            // Update the template context
            this.updateTemplateContext();
            // Notify the parent component that the data source has changed
            this.dataSourceChange.emit(ds.data);
            this._cd.detectChanges();
        });
    }
    ngAfterContentInit() {
        // if ng-template is provided inside the directive, create an embedded view
        // otherwise if not provided use the (dataSourceChange) event to get the data 
        // ex. 
        // <div fgbDataSource [dataSource]="mixedLoyaltyList$ | async" [sortOptions]="sortOptions" (dataSourceChange)="data = $event">
        //    {{ data | json }}
        // </div>
        if (this.template) {
            this.templateInstace = this.vc.createEmbeddedView(this.template, { $implicit: this.data, loaded: false, empty: this.isEmpty }).context;
        }
    }
    /**
     * Handles all the data source events and updates the data source
     */
    eventHandler(state, eventFn) {
        const ds = { ...state, ...eventFn(state) };
        return ds.update(ds.data, ds.sort);
    }
    /**
     * Handle the data source event changes
     */
    dataHandler(event) {
        // Create data source if data is changed
        return (state) => loadDataSource({ ...state, data: event });
    }
    /**
     * Handle the page event changes
     */
    pageHandler(pageNo) {
        return (state) => state.goToPage(pageNo);
    }
    /**
     * Update the template context
     */
    updateTemplateContext() {
        if (this.templateInstace) {
            this.templateInstace.$implicit = this.data;
            this.templateInstace.loaded = this.isLoaded;
            this.templateInstace.empty = this.isEmpty;
        }
    }
    /**
     * Handle the sort event
     */
    sortHandler(event) {
        return (state) => state.setSort(event);
    }
    get onDataSourceChanged() {
        return this._dataSubject.asObservable();
    }
    get onSortChanged() {
        return this._sortSubject.asObservable();
    }
    get onPageChanged() {
        return this._pageSubject.asObservable();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DataSourceDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.1", type: DataSourceDirective, isStandalone: true, selector: "[fgbDataSource]", inputs: { dataSource: "dataSource", sortOptions: "sortOptions", page: "page", pageSize: "pageSize" }, outputs: { dataSourceChange: "dataSourceChange", paginationChange: "paginationChange" }, queries: [{ propertyName: "template", first: true, predicate: TemplateRef, descendants: true, static: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: DataSourceDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fgbDataSource]',
                    standalone: true
                }]
        }], ctorParameters: () => [], propDecorators: { dataSource: [{
                type: Input
            }], sortOptions: [{
                type: Input
            }], page: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], dataSourceChange: [{
                type: Output
            }], paginationChange: [{
                type: Output
            }], template: [{
                type: ContentChild,
                args: [TemplateRef, { static: true }]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { ConfirmModalTermsDirective, CustomOwlCarousel, DataSourceDirective, DirectivesModule, HideMobileNav, IfItemEnabled, IfScreenSizeDirective, ImgBackgroundGrabberDirective, ImgDefaultDirective, MoreDropdownElement, NavBarElement, NavItemElement, NavbarDirectivesModule, NgSubscribeContext, NgSubscribeDirective, ScreenType, WebsiteContentFeatureFlagDirective, createDataSource, loadDataSource };
